import type { Service } from '../graphqlEnums'
import type { CreatePostInput, SchedulingType } from './graphqlTypes'
import type { Draft } from '~publish/legacy/composer/composer/entities/Draft'

import type { MapperContext } from './types'
import { mapAssets } from './mapAssets'
import { mapShareMode } from './mapShareMode'
import { serviceRegistry } from './metadata'

// The new mutations take a single channel, so an omni draft must be fanned out
// into per-service drafts before mapping.
export function resolveService(draft: Draft): Service {
  const { name } = draft.service
  if (name === 'omni') {
    throw new Error(
      '[composer/save] omni draft must be expanded into per-service drafts before mapping',
    )
  }
  return name
}

// The fields shared by create and edit (everything but the channel/post id).
// `metadata` is delegated to the per-service mapper; not-yet-implemented
// services throw NotImplemented rather than silently dropping data.
export function mapSharedPostFields(
  draft: Draft,
  ctx: MapperContext,
): Omit<CreatePostInput, 'channelId'> {
  const service = resolveService(draft)
  const { mode, dueAt, saveToDraft } = mapShareMode(ctx)

  // Reminder vs direct is per-draft (a channel can be a reminder while another
  // is automatic): legacy `reminder`/`direct` → GraphQL `notification`/`automatic`.
  const schedulingType: SchedulingType = draft.isReminder
    ? 'notification'
    : 'automatic'

  return {
    text: draft.text,
    mode,
    schedulingType,
    assets: mapAssets(draft),
    tagIds: ctx.selectedTagIds,
    aiAssisted: draft.aiAssisted,
    metadata: serviceRegistry[service].mapper(draft, ctx),
    ...(dueAt != null ? { dueAt } : {}),
    ...(saveToDraft != null ? { saveToDraft } : {}),
    ...(ctx.ideaId != null ? { ideaId: ctx.ideaId } : {}),
    ...(ctx.draftId != null ? { draftId: ctx.draftId } : {}),
    ...(ctx.source != null ? { source: ctx.source } : {}),
  }
}
